import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { combineLatest, distinctUntilChanged, map, Observable, Subscription } from 'rxjs';
import { ChatSession } from '../../models/chat-session.model';
import { ChatInteractiveSelection, ChatMessage } from '../../models/chat-message.model';
import { ChatPresetQuestion } from '../../models/chat-preset-question.model';
import { ChatUiError } from '../../models/chat-ui-error.model';
import { ChatPanelService } from '../../services/chat-panel.service';
import { ChatSessionService, ChatUsageBalance } from '../../services/chat-session.service';

@Component({
  selector: 'app-chat-shell',
  templateUrl: './chat-shell.component.html',
  styleUrls: ['./chat-shell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ChatShellComponent implements OnInit, OnDestroy {
  private readonly minShellWidth = 360;
  private readonly minShellHeight = 420;
  private readonly viewportGap = 12;

  sessions$: Observable<ChatSession[]>;
  activeSessionId$: Observable<string | null>;
  isSending$: Observable<boolean>;
  error$: Observable<ChatUiError | null>;
  usageBalance$: Observable<ChatUsageBalance | null>;
  presetQuestions$: Observable<ChatPresetQuestion[]>;
  presetQuestionsLoading$: Observable<boolean>;
  presetQuestionsLoadFailed$: Observable<boolean>;

  isOpen$: Observable<boolean>;
  isExpanded$: Observable<boolean>;

  messages$: Observable<ChatMessage[]>;
  hasMessages$: Observable<boolean>;
  hasInteractiveSelection$: Observable<boolean>;

  showMobileHistory = false;
  shellFrame: { left: number; top: number; width: number; height: number } | null = null;
  isDragging = false;
  isResizing = false;

  private openSubscription?: Subscription;
  private expandedSubscription?: Subscription;
  private pointerMoveListener?: (event: PointerEvent) => void;
  private pointerUpListener?: (event: PointerEvent) => void;

  constructor(
    private cdr: ChangeDetectorRef,
    private chatPanelService: ChatPanelService,
    private chatSessionService: ChatSessionService,
  ) {
    this.sessions$ = this.chatSessionService.sessionsChanges;
    this.activeSessionId$ = this.chatSessionService.activeSessionChanges;
    this.isSending$ = this.chatSessionService.sendingChanges;
    this.error$ = this.chatSessionService.errorChanges;
    this.usageBalance$ = this.chatSessionService.usageBalanceChanges;
    this.presetQuestions$ = this.chatSessionService.presetQuestionsChanges;
    this.presetQuestionsLoading$ = this.chatSessionService.presetQuestionsLoadingChanges;
    this.presetQuestionsLoadFailed$ = this.chatSessionService.presetQuestionsLoadFailedChanges;

    this.isOpen$ = this.chatPanelService.isOpen$;
    this.isExpanded$ = this.chatPanelService.isExpanded$;

    this.messages$ = combineLatest([
      this.sessions$,
      this.activeSessionId$,
    ]).pipe(
      map(([sessions, activeId]) => sessions.find((session) => session.id === activeId)?.messages || []),
    );

    this.hasMessages$ = this.messages$.pipe(map((messages) => (messages || []).length > 0));
    this.hasInteractiveSelection$ = this.messages$.pipe(
      map((messages) => this.hasPendingInteractiveSelection(messages || [])),
    );
  }

  ngOnInit(): void {
    this.openSubscription = this.isOpen$
      .pipe(
        distinctUntilChanged(),
      )
      .subscribe((isOpen) => {
        if (isOpen) {
          this.chatSessionService.refreshSessions();
          return;
        }

        this.showMobileHistory = false;
        this.shellFrame = null;
        this.isDragging = false;
        this.isResizing = false;
        this.stopShellInteraction();
      });

    this.expandedSubscription = this.isExpanded$
      .pipe(
        distinctUntilChanged(),
      )
      .subscribe((isExpanded) => {
        if (!isExpanded) {
          return;
        }

        this.shellFrame = null;
        this.isDragging = false;
        this.isResizing = false;
        this.stopShellInteraction();
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.openSubscription?.unsubscribe();
    this.expandedSubscription?.unsubscribe();
    this.stopShellInteraction();
    this.chatPanelService.close();
  }

  close() {
    this.showMobileHistory = false;
    this.shellFrame = null;
    this.isDragging = false;
    this.isResizing = false;
    this.chatPanelService.close();
  }

  toggleExpand() {
    this.shellFrame = null;
    this.chatPanelService.toggleExpand();
  }

  get shellStyle(): { [key: string]: string } | null {
    if (!this.shellFrame) {
      return null;
    }

    return {
      left: `${this.shellFrame.left}px`,
      top: `${this.shellFrame.top}px`,
      width: `${this.shellFrame.width}px`,
      height: `${this.shellFrame.height}px`,
      maxWidth: 'none',
      maxHeight: 'none',
      right: 'auto',
      bottom: 'auto',
      transform: 'none',
    };
  }

  startDrag(event: PointerEvent) {
    if (!this.canStartShellInteraction(event)) {
      return;
    }

    const frame = this.getCurrentShellFrame(event.currentTarget);
    if (!frame) {
      return;
    }

    event.preventDefault();
    this.isDragging = true;
    this.shellFrame = frame;

    const startX = event.clientX;
    const startY = event.clientY;
    const startLeft = frame.left;
    const startTop = frame.top;

    this.attachShellInteractionListeners(
      (moveEvent) => {
        const nextFrame = this.clampFrame({
          ...frame,
          left: startLeft + moveEvent.clientX - startX,
          top: startTop + moveEvent.clientY - startY,
        });

        this.shellFrame = nextFrame;
        this.cdr.markForCheck();
      },
      () => {
        this.isDragging = false;
        this.cdr.markForCheck();
      },
    );
  }

  startResize(event: PointerEvent, direction: string) {
    if (this.isSmallViewport()) {
      return;
    }

    const frame = this.getCurrentShellFrame(event.currentTarget);
    if (!frame) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.isResizing = true;
    this.shellFrame = frame;

    const startX = event.clientX;
    const startY = event.clientY;

    this.attachShellInteractionListeners(
      (moveEvent) => {
        const dx = moveEvent.clientX - startX;
        const dy = moveEvent.clientY - startY;
        let left = frame.left;
        let top = frame.top;
        let width = frame.width;
        let height = frame.height;

        if (direction.includes('e')) {
          width = frame.width + dx;
        }

        if (direction.includes('s')) {
          height = frame.height + dy;
        }

        if (direction.includes('w')) {
          width = Math.max(this.minShellWidth, frame.width - dx);
          left = frame.left + frame.width - width;
        }

        if (direction.includes('n')) {
          height = Math.max(this.minShellHeight, frame.height - dy);
          top = frame.top + frame.height - height;
        }

        const nextFrame = this.clampFrame({ left, top, width, height });
        this.shellFrame = nextFrame;
        this.cdr.markForCheck();
      },
      () => {
        this.isResizing = false;
        this.cdr.markForCheck();
      },
    );
  }

  startNewChat() {
    this.chatSessionService.startNewSession();
    this.showMobileHistory = false;
  }

  openHistoryFullPage() {
    this.showMobileHistory = true;
    this.shellFrame = null;
    this.isDragging = false;
    this.isResizing = false;
    this.stopShellInteraction();
    this.chatPanelService.openExpanded();
  }

  selectSession(sessionId: string) {
    this.chatSessionService.selectSession(sessionId);
    this.showMobileHistory = false;
  }

  toggleMobileHistory() {
    this.showMobileHistory = !this.showMobileHistory;
  }

  deleteSession(sessionId: string) {
    this.chatSessionService.deleteSession(sessionId);
  }

  sendMessage(message: string) {
    this.chatSessionService.sendMessage(message);
  }

  retryAssistantMessage(message: ChatMessage) {
    this.chatSessionService.retryAssistantMessage(message.id);
  }

  rateAssistantMessage(event: { message: ChatMessage; feedback: 'good' | 'bad' | null }) {
    this.chatSessionService.rateAssistantMessage(event.message.id, event.feedback);
  }

  sendPresetQuestion(preset: ChatPresetQuestion) {
    this.chatSessionService.sendMessage(preset.label, preset.id);
  }

  sendInteractiveSelection(selection: ChatInteractiveSelection) {
    this.chatSessionService.sendMessage(selection.value, undefined, selection);
  }

  trackByPresetQuestionId(index: number, preset: ChatPresetQuestion) {
    return preset.id || index;
  }

  clearError() {
    this.chatSessionService.clearError();
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (!this.shellFrame) {
      return;
    }

    if (this.isSmallViewport()) {
      this.shellFrame = null;
      this.stopShellInteraction();
      this.cdr.markForCheck();
      return;
    }

    this.shellFrame = this.clampFrame(this.shellFrame);
    this.cdr.markForCheck();
  }

  formatRemainingTokens(balance: ChatUsageBalance | null) {
    if (!balance) {
      return '--';
    }

    return Math.max(0, Math.floor(balance.remainingTokens || 0)).toLocaleString();
  }

  private hasPendingInteractiveSelection(messages: ChatMessage[]): boolean {
    const latestAssistantMessage = [...messages]
      .reverse()
      .find((message) => message.role === 'assistant');

    if (!latestAssistantMessage?.interactive) {
      return false;
    }

    return !!(
      latestAssistantMessage.interactive.products?.length ||
      latestAssistantMessage.interactive.facilities?.length ||
      latestAssistantMessage.interactive.woredas?.length ||
      latestAssistantMessage.interactive.zones?.length ||
      latestAssistantMessage.interactive.hubs?.length ||
      latestAssistantMessage.interactive.regions?.length
    );
  }

  private canStartShellInteraction(event: PointerEvent): boolean {
    if (this.isSmallViewport()) {
      return false;
    }

    const target = event.target as HTMLElement | null;
    return !target?.closest('button, a, input, textarea, [data-no-drag], .chat-actions');
  }

  private attachShellInteractionListeners(
    onMove: (event: PointerEvent) => void,
    onEnd: (event: PointerEvent) => void,
  ): void {
    this.stopShellInteraction();

    this.pointerMoveListener = (event) => {
      event.preventDefault();
      onMove(event);
    };

    this.pointerUpListener = (event) => {
      this.stopShellInteraction();
      onEnd(event);
    };

    document.addEventListener('pointermove', this.pointerMoveListener);
    document.addEventListener('pointerup', this.pointerUpListener);
    document.addEventListener('pointercancel', this.pointerUpListener);
  }

  private stopShellInteraction(): void {
    if (this.pointerMoveListener) {
      document.removeEventListener('pointermove', this.pointerMoveListener);
      this.pointerMoveListener = undefined;
    }

    if (this.pointerUpListener) {
      document.removeEventListener('pointerup', this.pointerUpListener);
      document.removeEventListener('pointercancel', this.pointerUpListener);
      this.pointerUpListener = undefined;
    }
  }

  private getCurrentShellFrame(origin: EventTarget | null) {
    const shell = (origin as HTMLElement | null)?.closest('.chat-shell') as HTMLElement | null;
    if (!shell) {
      return null;
    }

    const rect = shell.getBoundingClientRect();
    return this.clampFrame({
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    });
  }

  private clampFrame(frame: { left: number; top: number; width: number; height: number }) {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const maxWidth = Math.max(this.minShellWidth, viewportWidth - this.viewportGap * 2);
    const maxHeight = Math.max(this.minShellHeight, viewportHeight - this.viewportGap * 2);
    const width = Math.min(Math.max(frame.width, this.minShellWidth), maxWidth);
    const height = Math.min(Math.max(frame.height, this.minShellHeight), maxHeight);
    const maxLeft = Math.max(this.viewportGap, viewportWidth - width - this.viewportGap);
    const maxTop = Math.max(this.viewportGap, viewportHeight - height - this.viewportGap);
    const left = Math.min(Math.max(frame.left, this.viewportGap), maxLeft);
    const top = Math.min(Math.max(frame.top, this.viewportGap), maxTop);

    return { left, top, width, height };
  }

  private isSmallViewport(): boolean {
    return window.innerWidth <= 520;
  }
}
